import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Link,
  Skeleton,
  IconButton,
  Chip
} from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';

// 🔥 NEW: 匯入影片相關元件
import { 
  isVideoMedia, 
  VideoThumbnail, 
  VideoPlaceholder, 
  VideoPlayerModal 
} from './VideoPlayer';

// Helper function for timestamp formatting
const formatTimestamp = (timestamp, format = 'time-only') => {
  const date = new Date(timestamp);
  if (format === 'time-only') {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleString();
};

// Extract the bubble content into a separate component
const MessageBubbleContent = ({ 
  message, 
  isConsecutive = false,
  isOnLeft,
  isOnRight,
  getAvatarColor,
  getAvatarLetter,
  getSenderDisplayName,
  renderTextWithLinks,
  getImageUrls,
  onImageClick,
  onVideoClick, // 🔥 NEW: 影片點擊處理
  effectiveContentType // 🔥 NEW: 有效的內容類型（可能被覆蓋為 video）
}) => {
  const [imageLoading, setImageLoading] = useState({});
  const [imageError, setImageError] = useState({});
  
  const {
    content,
    contentType = 'text',
    senderType,
    timestamp,
    isRead,
    metadata = {},
    images = [],
    hasImages = false,
    imageCount = 0
  } = message;
  
  // 🔥 使用 effectiveContentType 如果有提供，否則使用原始 contentType
  const actualContentType = effectiveContentType || contentType;
  
  const isSystem = senderType === 'system';
  const parsedMeta = typeof metadata === 'string' ? (() => { try { return JSON.parse(metadata); } catch { return {}; } })() : (metadata || {});
  const isEscalation = isSystem && parsedMeta.type === 'escalated';

  // 處理圖片載入狀態
  const handleImageLoad = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
  };
  
  const handleImageError = (imageId) => {
    setImageLoading(prev => ({ ...prev, [imageId]: false }));
    setImageError(prev => ({ ...prev, [imageId]: true }));
  };
  
  // 開啟圖片檢視器
  const openImageViewer = (images, startIndex = 0) => {
    if (onImageClick) {
      const formattedImages = images.map((img, index) => {
        if (typeof img === 'string') {
          return {
            url: img,
            name: `圖片 ${index + 1}`,
            preview: img,
            fallbackUrl: null
          };
        } else {
          return {
            url: img.url || img.imageUrl || img.s3Url || img.directUrl || img,
            name: img.filename || img.displayName || img.metadata?.originalName || img.name || `圖片 ${index + 1}`,
            preview: img.thumbnailUrl || img.thumbnail || img.url || img.imageUrl || img.s3Url || img.directUrl,
            fallbackUrl: img.thumbnailUrl || img.thumbnail || null
          };
        }
      });
      
      onImageClick(formattedImages, startIndex);
    }
  };
  
  // 渲染單一圖片（原有功能保持不變）
  const renderSingleImageLocal = (imageData, imageId = 'main', clickIndex = 0, allImages = null) => {
    // 🔥 NEW: 檢查是否為影片，若是則使用 VideoThumbnail
    if (isVideoMedia(imageData)) {
      return (
        <VideoThumbnail
          videoData={imageData}
          videoId={imageId}
          metadata={metadata}
          onClick={onVideoClick}
        />
      );
    }
    
    // === 以下為原有的圖片處理邏輯，完全保持不變 ===
    
    // 處理不同格式的圖片資料
    let url, thumbnail, imageMeta;
    
    if (typeof imageData === 'string') {
      url = imageData;
      thumbnail = null;
      imageMeta = {};
    } else {
      url = imageData.url || imageData.imageUrl || imageData.s3Url || imageData.directUrl;
      thumbnail = imageData.thumbnailUrl || imageData.thumbnail;
      imageMeta = imageData.metadata || imageData;
    }
    
    const isLoading = imageLoading[imageId] !== false;
    const hasError = imageError[imageId];
    const displayUrl = thumbnail || url;
    
    return (
      <Box
        sx={{
          position: 'relative',
          borderRadius: 1,
          overflow: 'hidden',
          cursor: onImageClick ? 'pointer' : 'default',
          maxWidth: '300px',
          bgcolor: 'grey.100',
          '&:hover .image-overlay': {
            opacity: onImageClick ? 1 : 0
          }
        }}
        onClick={() => {
          if (!hasError && onImageClick) {
            const imagesToView = allImages || [imageData];
            // 過濾掉影片，只保留圖片
            const onlyImages = imagesToView.filter(img => !isVideoMedia(img));
            if (onlyImages.length > 0) {
              openImageViewer(onlyImages, clickIndex);
            }
          }
        }}
      >
        {/* 載入中的骨架屏 */}
        {isLoading && (
          <Skeleton
            variant="rectangular"
            width={300}
            height={200}
            animation="wave"
          />
        )}
        
        {/* 錯誤狀態 */}
        {hasError ? (
          <Box
            sx={{
              width: 300,
              height: 200,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'grey.200'
            }}
          >
            <BrokenImageIcon sx={{ fontSize: 48, color: 'grey.500', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              圖片載入失敗
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              component="img"
              src={displayUrl}
              alt={imageMeta.filename || imageMeta.originalName || "圖片訊息"}
              sx={{
                maxWidth: '100%',
                maxHeight: '300px',
                objectFit: 'contain',
                display: isLoading ? 'none' : 'block'
              }}
              onLoad={() => handleImageLoad(imageId)}
              onError={() => handleImageError(imageId)}
              crossOrigin="anonymous"
            />
            
            {/* 圖片操作覆蓋層 */}
            {onImageClick && (
              <Box
                className="image-overlay"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: 0,
                  transition: 'opacity 0.3s'
                }}
              >
                <IconButton
                  size="large"
                  sx={{ 
                    color: 'white',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <ZoomInIcon fontSize="large" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };
  
  // 渲染圖片/影片網格（原有功能保持不變）
  const renderImageGridLocal = (imageArray) => {
    const gridCols = imageArray.length === 1 ? 1 : 2;
    
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gap: 1,
          maxWidth: imageArray.length === 1 ? '300px' : '400px'
        }}
      >
        {imageArray.map((img, index) => {
          const imageId = (typeof img === 'object' && img.id) ? img.id : `img-${index}`;
          
          return (
            <Box key={imageId}>
              {renderSingleImageLocal(
                img,
                imageId,
                index,
                imageArray
              )}
            </Box>
          );
        })}
      </Box>
    );
  };
  
  // 渲染訊息內容
  const renderMessageContent = () => {
    const imageUrls = getImageUrls();
    const hasImageContent = imageUrls.length > 0 || contentType === 'image' || hasImages || imageCount > 0;
    
    // 🔥 CRITICAL FIX: 影片偵測邏輯
    // 後端廣播可能不包含 contentType: 'video'，需要從其他線索判斷
    const isVideoMessage = (() => {
      // 1. 明確的 contentType
      if (contentType === 'video') return true;
      
      // 2. content 以 [影片] 開頭
      if (content && content.startsWith('[影片]')) return true;
      
      // 3. 檢查 URL 是否包含 .mp4
      for (const img of imageUrls) {
        const url = typeof img === 'string' ? img : (img?.url || img?.s3Url || '');
        if (url && (url.toLowerCase().includes('.mp4') || 
                    url.toLowerCase().includes('.webm') ||
                    url.toLowerCase().includes('.mov'))) {
          return true;
        }
      }
      
      // 4. 檢查 mimeType
      for (const img of imageUrls) {
        if (typeof img === 'object' && img.mimeType && img.mimeType.startsWith('video/')) {
          return true;
        }
      }
      
      return false;
    })();
    
    // 🔥 處理影片訊息
    if (isVideoMessage) {
      // 從 images 陣列取得影片資料
      const videoData = imageUrls.length > 0 ? imageUrls[0] : null;
      
      if (videoData) {
        return (
          <VideoThumbnail
            videoData={videoData}
            videoId="video-main"
            metadata={metadata}
            onClick={onVideoClick}
          />
        );
      }
      
      // 如果沒有影片資料，顯示 metadata 中的縮圖
      if (metadata?.thumbnailUrl) {
        return (
          <VideoThumbnail
            videoData={{
              url: metadata?.s3Url || metadata?.url || metadata?.videoUrl || null,
              thumbnailUrl: metadata.thumbnailUrl,
              metadata: metadata
            }}
            videoId="video-meta"
            metadata={metadata}
            onClick={onVideoClick}
          />
        );
      }
      
      // 回退顯示
      return <VideoPlaceholder content={content} metadata={metadata} />;
    }
    
    // === ChatFlow 完成摘要卡片 ===
    if (metadata?.chatflowSummary) {
      const { flowName, collectedData: summaryData, stepsMapping: summarySteps } = metadata;
      return (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <AssignmentTurnedInIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.dark' }}>
              {flowName || '對話流程'} — 完成摘要
            </Typography>
          </Box>
          {summaryData && Object.entries(summaryData).map(([key, val]) => {
            const stepInfo = summarySteps?.[key];
            const label = stepInfo?.label || key;
            const displayVal = (stepInfo?.stepType === 'image_upload' && Array.isArray(val))
              ? (val.length === 0 ? '跳過' : `${val.length} 張圖片`)
              : (stepInfo?.stepType === 'confirm' ? '完成' : String(val));
            return (
              <Box key={key} sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'baseline' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 'fit-content', fontSize: '14px' }}>
                  {label}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '14px', wordBreak: 'break-word' }}>
                  {displayVal}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    }

    // === ChatFlow 步驟訊息（帶流程名稱 Chip） ===
    if (metadata?.chatflow && !metadata?.chatflowSummary && contentType === 'text' && content && content.trim()) {
      const chipColor = metadata?.isPostback ? 'primary' : 'default';
      const chipLabel = metadata?.flowName || 'ChatFlow';
      return (
        <Box>
          <Chip
            label={chipLabel}
            size="small"
            color={chipColor}
            variant="outlined"
            sx={{ mb: 0.5, height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.8 } }}
          />
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              fontSize: { xs: '17px', md: '16px' },
              fontStyle: isSystem ? 'italic' : 'normal'
            }}
          >
            {renderTextWithLinks(content)}
          </Typography>
        </Box>
      );
    }

    // === 以下為原有的訊息處理邏輯，完全保持不變 ===

    // 處理純文字
    if (contentType === 'text' && content && content.trim()) {
      return (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5,
            fontSize: { xs: '17px', md: '16px' },
            fontStyle: isSystem ? 'italic' : 'normal'
          }}
        >
          {renderTextWithLinks(content)}
        </Typography>
      );
    }
    
    // 處理圖片（可能包含影片）
    if (contentType === 'image' && imageUrls.length > 0) {
      return renderImageGridLocal(imageUrls);
    }
    
    // 處理貼圖
    if (contentType === 'sticker') {
      return (
        <Box 
          sx={{
            p: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {metadata.stickerUrl ? (
            <Box 
              component="img"
              src={metadata.stickerUrl}
              alt="貼圖"
              sx={{
                width: '100px',
                height: '100px',
                objectFit: 'contain'
              }}
            />
          ) : (
            <Typography variant="body2">
              {content || '[LINE 貼圖]'}
            </Typography>
          )}
        </Box>
      );
    }
    
    // 預設回退
    return (
      <Typography 
        variant="body2"
        sx={{
          fontSize: { xs: '17px', md: '16px' },
          fontStyle: isSystem ? 'italic' : 'normal'
        }}
      >
        {content || `[${contentType} 訊息]`}
      </Typography>
    );
  };
  
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOnRight ? 'flex-end' : 'flex-start',
          mb: isConsecutive ? 0.5 : 1.5,
          maxWidth: '100%'
        }}
      >
        {/* 發送者資訊 - 系統訊息顯示特殊標籤 */}
        {!isConsecutive && !isSystem && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
              ml: isOnRight ? 0 : 1,
              mr: isOnRight ? 1 : 0
            }}
          >
            {isOnLeft && (
              <Avatar
                sx={{ 
                  width: 20, 
                  height: 20,
                  mr: 0.5,
                  fontSize: '0.7rem',
                  bgcolor: getAvatarColor()
                }}
              >
                {getAvatarLetter()}
              </Avatar>
            )}
            
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem' }}
            >
              {getSenderDisplayName()}
            </Typography>
            
            {isOnRight && (
              <Avatar
                sx={{ 
                  width: 20, 
                  height: 20,
                  ml: 0.5,
                  fontSize: '0.7rem',
                  bgcolor: getAvatarColor()
                }}
              >
                {getAvatarLetter()}
              </Avatar>
            )}
          </Box>
        )}
        
        {/* 系統訊息標籤 */}
        {!isConsecutive && isSystem && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 0.5,
              mr: 1
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: '0.75rem',
                color: isEscalation ? 'error.main' : 'warning.main',
                mr: 0.5
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.65rem',
                color: isEscalation ? 'error.main' : 'warning.main',
                fontStyle: 'italic',
                fontWeight: isEscalation ? 600 : 'normal'
              }}
            >
              {isEscalation ? '轉交訊息' : '系統訊息'}
            </Typography>
          </Box>
        )}

        {/* 訊息氣泡 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isOnRight ? 'row-reverse' : 'row',
            alignItems: 'flex-end',
            maxWidth: isSystem ? '90%' : '85%',
            width: '100%'
          }}
        >
          {/* 訊息內容 */}
          <Paper
            elevation={isSystem ? 0 : 1}
            sx={{
              p: 1.5,
              borderRadius: 2,
              maxWidth: '100%',
              // 系統訊息的特殊樣式
              bgcolor: isEscalation
                ? 'rgba(211, 47, 47, 0.06)'
                : isSystem
                  ? 'rgba(255, 193, 7, 0.08)'
                  : (isOnRight ? '#fafafa' : 'grey.100'),
              border: isSystem
                ? '1px dashed'
                : (isOnRight ? '2px solid' : 'none'),
              borderColor: isEscalation
                ? 'error.main'
                : isSystem
                  ? 'warning.main'
                  : (isOnRight ? getAvatarColor() : 'transparent'),
              color: isSystem ? 'text.primary' : 'text.primary',
              ml: isOnRight ? 0 : isConsecutive ? 3 : 0,
              mr: isOnRight ? isConsecutive ? 3 : 0 : 0,
              position: 'relative',
              // 系統訊息不需要對話框尾巴
              '&::before': !isConsecutive && !isSystem ? {
                content: '""',
                position: 'absolute',
                width: 0,
                height: 0,
                border: '6px solid transparent',
                ...(isOnRight ? {
                  borderLeftColor: getAvatarColor(),
                  right: -12,
                  top: 8
                } : {
                  borderRightColor: 'grey.100',
                  left: -12,
                  top: 8
                })
              } : {}
            }}
          >
            {renderMessageContent()}
          </Paper>

          {/* 時間與已讀狀態 */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isOnRight ? 'flex-start' : 'flex-end',
              mx: 1,
              minWidth: 'max-content'
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.6rem',
                whiteSpace: 'nowrap'
              }}
            >
              {formatTimestamp(timestamp, 'time-only')}
            </Typography>
            
            {isOnRight && !isSystem && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.55rem',
                  color: isRead ? 'success.main' : 'text.disabled'
                }}
              >
                {isRead ? '已讀' : '未讀'}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

// 主要 MessageBubble 元件
const MessageBubble = ({ message, isConsecutive = false, onImageClick }) => {
  // 🔥 NEW: 影片播放器狀態
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);

  if (!message) return null;

  const {
    content,
    contentType = 'text',
    senderType,
    timestamp,
    isRead,
    metadata = {},
    imageUrl,
    thumbnailUrl,
    images = [],
    hasImages = false,
    imageCount = 0,
    direction,
    // 🔥 NEW: 從 Socket 廣播接收的欄位
    firstImageUrl,
    allImageUrls
  } = message;

  // 🔥 NEW: 影片點擊處理
  const handleVideoClick = (videoData) => {
    setCurrentVideo(videoData);
    setVideoPlayerOpen(true);
  };

  // Extract image/video URLs（原有功能保持不變）
  const getImageUrls = () => {
    const urls = [];
    
    // 🔥 NEW: 檢查 firstImageUrl（來自 Socket 廣播）
    if (message.firstImageUrl) {
      urls.push({
        url: message.firstImageUrl,
        id: 'first-image',
        mimeType: message.firstImageUrl.includes('.mp4') ? 'video/mp4' : 'image/jpeg',
        metadata: metadata
      });
    }
    
    // 🔥 NEW: 檢查 allImageUrls（來自 Socket 廣播）
    if (message.allImageUrls && Array.isArray(message.allImageUrls) && message.allImageUrls.length > 0) {
      message.allImageUrls.forEach((imgUrl, index) => {
        // 避免重複添加 firstImageUrl
        if (imgUrl !== message.firstImageUrl) {
          urls.push({
            url: imgUrl,
            id: `all-image-${index}`,
            mimeType: imgUrl.includes('.mp4') ? 'video/mp4' : 'image/jpeg',
            metadata: metadata
          });
        }
      });
    }
    
    if (imageUrl) {
      urls.push(imageUrl);
    }
    
    if (images && Array.isArray(images) && images.length > 0) {
      images.forEach(img => {
        if (typeof img === 'string') {
          urls.push(img);
        } else if (img && typeof img === 'object') {
          const url = img.url || img.imageUrl || img.s3Url || img.directUrl;
          if (url) {
            urls.push({
              url: url,
              id: img.id,
              filename: img.filename || img.displayName,
              metadata: img.metadata || img,
              thumbnailUrl: img.thumbnailUrl || img.thumbnail,
              mimeType: img.mimeType // 🔥 保留 mimeType 用於判斷影片
            });
          }
        }
      });
    }
    
    if (metadata) {
      if (metadata.imageUrl) {
        urls.push(metadata.imageUrl);
      }
      if (metadata.s3Url) {
        urls.push(metadata.s3Url);
      }
      
      if (metadata.images && Array.isArray(metadata.images) && metadata.images.length > 0) {
        metadata.images.forEach(img => {
          if (typeof img === 'string') {
            urls.push(img);
          } else if (img && typeof img === 'object') {
            const url = img.url || img.imageUrl || img.s3Url || img.directUrl;
            if (url) {
              urls.push({
                url: url,
                id: img.id,
                filename: img.filename || img.displayName,
                metadata: img.metadata || img,
                thumbnailUrl: img.thumbnailUrl || img.thumbnail,
                mimeType: img.mimeType
              });
            }
          }
        });
      }
    }
    
    // 去重
    const uniqueUrls = [];
    const seenUrls = new Set();
    
    urls.forEach(item => {
      const url = typeof item === 'string' ? item : item.url;
      if (url && !seenUrls.has(url)) {
        seenUrls.add(url);
        uniqueUrls.push(item);
      }
    });
    
    return uniqueUrls;
  };

  // === 以下為原有的位置判斷邏輯，完全保持不變 ===
  
  const isTestMode = metadata?.testMode === true;
  const isFaqTest = metadata?.source === 'faq_test';
  const isFaqWidget = metadata?.source === 'faq_widget' && !isTestMode;
  
  const isInTestPage = typeof window !== 'undefined' && 
    (window.location.pathname.includes('/test_') || 
     window.location.pathname.includes('/faq-test') ||
     window.location.pathname.includes('/admin'));
  
  //update for COnversationDrawer
  const isInOmniChat = (typeof window !== 'undefined' && 
    window.location.pathname.includes('/omnichat')) ||
    metadata?.viewContext === 'omnichat';


  const isInFaqWidgetComponent = typeof window !== 'undefined' && 
    !isInOmniChat && 
    !isInTestPage && 
    (window.location.pathname === '/' || 
     window.location.pathname.includes('/widget') ||
     document.querySelector('[class*="FaqChatWidget"]') !== null);
  
  const isCustomer = senderType === 'customer';
  const isUser = senderType === 'user';
  const isAgent = senderType === 'agent';
  const isAI = senderType === 'ai' || senderType === 'assistant' || senderType === 'bot';
  const isSystem = senderType === 'system';
  
  let isOnLeft, isOnRight;
  
  // 🔥 NEW: 自動偵測影片類型
  // 如果 contentType 不是 'video'，但 URL 包含 .mp4，則視為影片
  const detectContentType = () => {
    if (contentType === 'video') return 'video';
    if (contentType === 'image') {
      // 檢查是否實際上是影片
      const urls = [
        firstImageUrl,
        ...(allImageUrls || []),
        ...(images || []).map(img => typeof img === 'string' ? img : (img?.url || img?.s3Url || ''))
      ].filter(Boolean);
      
      for (const url of urls) {
        if (typeof url === 'string' &&
            (url.toLowerCase().includes('.mp4') ||
             url.toLowerCase().includes('.webm') ||
             url.toLowerCase().includes('.mov'))) {
          return 'video';
        }
      }
    }
    // 檢查 content 是否為 [影片]
    if (content && content.startsWith('[影片]')) {
      return 'video';
    }
    return contentType;
  };
  
  const effectiveContentType = detectContentType();
  
  if (isTestMode || isFaqTest || isInTestPage) {
    isOnLeft = isAI || isAgent;
    isOnRight = isCustomer || isUser || isSystem;
  } else if (isInFaqWidgetComponent && isFaqWidget) {
    isOnLeft = isAI || isAgent;
    isOnRight = isUser || isCustomer || isSystem;
  } else if (isInOmniChat) {
    if (direction) {
      isOnLeft = direction === 'incoming';
      isOnRight = direction === 'outgoing';
    } else {
      if (isFaqWidget) {
        isOnLeft = isCustomer || isUser;
        isOnRight = isAI || isAgent || isSystem;
      } else {
        isOnLeft = isCustomer || isUser;
        isOnRight = isAgent || isAI || isSystem;
      }
    }
  } else {
    isOnLeft = isAI || isAgent;
    isOnRight = isUser || isCustomer || isSystem;
  }
  
  const getSenderDisplayName = () => {
    switch (senderType) {
      case 'agent':
        return '客服';
      case 'ai':
      case 'assistant':
      case 'bot':
        return 'AI助理';
      case 'user':
        return '用戶';
      case 'customer':
        return '客戶';
      case 'system':
        return '系統';
      default:
        return senderType || '未知';
    }
  };

  const getAvatarColor = () => {
    if (isUser || isCustomer) return 'secondary.main';
    if (senderType === 'agent') return 'primary.main';
    if (senderType === 'ai' || senderType === 'assistant' || senderType === 'bot') return 'info.main';
    if (senderType === 'system') return 'warning.main';
    return 'grey.500';
  };

  const getAvatarLetter = () => {
    switch (senderType) {
      case 'agent':
        return '客';
      case 'ai':
      case 'assistant':
      case 'bot':
        return 'AI';
      case 'user':
        return '用';
      case 'customer':
        return '客';
      case 'system':
        return '系';
      default:
        return '?';
    }
  };

  const renderTextWithLinks = (text) => {
    if (!text || typeof text !== 'string') return text;

    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        const url = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <Link
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: isOnRight ? 'inherit' : 'primary.main',
              textDecoration: 'underline',
              '&:hover': {
                textDecoration: 'none'
              }
            }}
          >
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  // 檢查是否同時有文字和圖片/影片
  const imageUrls = getImageUrls();
  const hasTextContent = content && content.trim();
  const hasImageContent = imageUrls.length > 0;
  const hasTextAndImages = hasTextContent && hasImageContent && contentType === 'image';

  // 如果有文字和圖片，分開渲染
  if (hasTextAndImages) {
    return (
      <>
        {/* 文字氣泡 */}
        <MessageBubbleContent
          message={{
            ...message,
            contentType: 'text',
            images: [],
            hasImages: false,
            imageCount: 0
          }}
          isConsecutive={isConsecutive}
          isOnLeft={isOnLeft}
          isOnRight={isOnRight}
          getAvatarColor={getAvatarColor}
          getAvatarLetter={getAvatarLetter}
          getSenderDisplayName={getSenderDisplayName}
          renderTextWithLinks={renderTextWithLinks}
          getImageUrls={() => []}
          onImageClick={onImageClick}
          onVideoClick={handleVideoClick}
        />
        
        {/* 圖片/影片氣泡 */}
        <MessageBubbleContent
          message={{
            ...message,
            content: '',
            contentType: 'image'
          }}
          isConsecutive={true}
          isOnLeft={isOnLeft}
          isOnRight={isOnRight}
          getAvatarColor={getAvatarColor}
          getAvatarLetter={getAvatarLetter}
          getSenderDisplayName={getSenderDisplayName}
          renderTextWithLinks={renderTextWithLinks}
          getImageUrls={getImageUrls}
          onImageClick={onImageClick}
          onVideoClick={handleVideoClick}
        />

        {/* 🔥 NEW: 影片播放器 */}
        <VideoPlayerModal
          open={videoPlayerOpen}
          onClose={() => setVideoPlayerOpen(false)}
          video={currentVideo}
        />
      </>
    );
  }

  // 正常單一氣泡渲染
  return (
    <>
      <MessageBubbleContent
        message={message}
        isConsecutive={isConsecutive}
        isOnLeft={isOnLeft}
        isOnRight={isOnRight}
        getAvatarColor={getAvatarColor}
        getAvatarLetter={getAvatarLetter}
        getSenderDisplayName={getSenderDisplayName}
        renderTextWithLinks={renderTextWithLinks}
        getImageUrls={getImageUrls}
        onImageClick={onImageClick}
        onVideoClick={handleVideoClick}
      />

      {/* 🔥 NEW: 影片播放器 */}
      <VideoPlayerModal
        open={videoPlayerOpen}
        onClose={() => setVideoPlayerOpen(false)}
        video={currentVideo}
      />
    </>
  );
};

export default MessageBubble;